/**
 * ProcedureOutputField - Output component for procedure array field
 *
 * Displays procedure output data with collapsible sections for coverage, PA, etc.
 * Each section is a flat, top-level accordion item matching the schema structure.
 *
 * Exports:
 * - ProcedureOutputField: Main output component
 * - ProcedureOutputFieldPreview: Preview component for table cells
 */

import { Accordion } from '@components/ui/accordion';
import { SectionScoreBadge } from '@components/ui/disposition-status';
import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

import { computeFieldScore } from '@/utils/dispositionCompleteness';
import { getCallSourceId } from '@/utils/evidence';

import { ItemDispositionProvider, ReviewProvider, SchemaProvider } from './context';
import { PillPreviewList, type PreviewPill } from './PillPreviewList';
import {
  AppealSection,
  CoverageSection,
  DenialSection,
  PreDeterminationProcessSection,
  PreDeterminationStatusSection,
  PriorAuthProcessSection,
  PriorAuthStatusSection,
} from './sections';
import type { OutputArrayProps, PreviewProps } from './types';
import { type CloneableItem, CloneControls, useCloneItems } from './useCloneItems';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Procedure output item shape (defined locally, schema provides field definitions at runtime)
 */
interface ProcedureItem {
  code?: string;
  name?: string;
  coverage?: Record<string, unknown>;
  prior_auth_status?: Record<string, unknown>;
  prior_auth_process?: Record<string, unknown>;
  pre_determination_status?: Record<string, unknown>;
  pre_determination_process?: Record<string, unknown>;
  appeal?: Record<string, unknown>;
  denial?: Record<string, unknown>;
}

// =============================================================================
// MAIN OUTPUT COMPONENT
// =============================================================================

export const ProcedureOutputField: React.FC<OutputArrayProps> = ({
  data,
  outputMetadata,
  extractedMetadata,
  extractedData,
  onNavigateToCall,
  fieldId = 'procedure',
  isHumanAudit,
  onAcceptExtracted,
  dispositionFields,
  currentCallId,
  onChange,
}) => {
  const [expandedProcedures, setExpandedProcedures] = useState<string[]>([]);

  // Cast data to expected shape (schema provides field definitions at runtime)
  const items = (Array.isArray(data) ? data : []) as ProcedureItem[];

  // Clone-mode controller (see useCloneItems). Enabled only when a write path
  // (onChange) is wired; needs at least two items to have somewhere to clone to.
  const clone = useCloneItems(items as CloneableItem[], onChange);
  const cloneEnabled = Boolean(onChange) && items.length > 1;

  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic py-2">No procedure data available</div>
    );
  }

  const toggleProcedure = (procedureId: string) => {
    setExpandedProcedures(prev =>
      prev.includes(procedureId) ? prev.filter(id => id !== procedureId) : [...prev, procedureId],
    );
  };

  const outputFieldMeta = outputMetadata?.fields?.[fieldId] as any;
  const extractedFieldMeta = extractedMetadata?.fields?.[fieldId] as any;
  const outputSourceCallId = getCallSourceId(outputFieldMeta);
  const extractedSourceCallId = getCallSourceId(extractedFieldMeta);
  const extractedDataArray = Array.isArray(extractedData) ? extractedData : [];

  return (
    <SchemaProvider>
      <div className="space-y-4">
        {items.map((procedure, index) => {
          const procedureId = `procedure-${index}`;
          const isExpanded = expandedProcedures.includes(procedureId);
          const procedureLabel = procedure.code || procedure.name || `Procedure ${index + 1}`;
          // Whole-item captured/required rollup (procedure[N].* across all subsections),
          // mirroring the per-subsection badges inside.
          const itemScore = computeFieldScore(
            `${fieldId}[${index}]`,
            dispositionFields?.required_fields,
            dispositionFields?.missing_fields,
          );

          return (
            <ItemDispositionProvider
              key={procedureId}
              arrayName={fieldId}
              index={index}
              dispositionFields={dispositionFields}
            >
              <ReviewProvider
                outputItemMeta={outputFieldMeta?.[index]}
                outputSourceCallId={outputSourceCallId}
                extractedItemMeta={extractedFieldMeta?.[index]}
                extractedSourceCallId={extractedSourceCallId}
                extractedItemData={extractedDataArray[index]}
                onNavigateToCall={onNavigateToCall}
                isHumanAudit={isHumanAudit}
                onAcceptExtracted={onAcceptExtracted}
                currentCallId={currentCallId}
              >
                <div className="border rounded-md bg-muted/30">
                  {/* Procedure Header */}
                  <div className="flex items-center gap-2 pr-2">
                    <button
                      type="button"
                      onClick={() => toggleProcedure(procedureId)}
                      className="flex-1 flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors text-left rounded-md"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium">{procedureLabel}</span>
                        <SectionScoreBadge score={itemScore} />
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-muted-foreground transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <CloneControls
                      index={index}
                      clone={cloneEnabled ? clone : null}
                      entityLabel="procedure"
                    />
                  </div>

                  {/* Procedure Content - Flat Accordion Sections */}
                  {isExpanded && (
                    <Accordion
                      type="multiple"
                      defaultValue={[`${procedureId}-coverage`, `${procedureId}-prior-auth-status`]}
                      className="border-t"
                    >
                      <CoverageSection
                        data={procedure.coverage}
                        sectionId={`${procedureId}-coverage`}
                        entityType="procedure"
                      />
                      <PriorAuthStatusSection
                        data={procedure.prior_auth_status}
                        sectionId={`${procedureId}-prior-auth-status`}
                        entityType="procedure"
                      />
                      <PriorAuthProcessSection
                        data={procedure.prior_auth_process}
                        sectionId={`${procedureId}-prior-auth-process`}
                        entityType="procedure"
                      />
                      <PreDeterminationStatusSection
                        data={procedure.pre_determination_status}
                        sectionId={`${procedureId}-pre-determination-status`}
                        entityType="procedure"
                      />
                      <PreDeterminationProcessSection
                        data={procedure.pre_determination_process}
                        sectionId={`${procedureId}-pre-determination-process`}
                        entityType="procedure"
                      />
                      <AppealSection
                        data={procedure.appeal}
                        sectionId={`${procedureId}-appeal`}
                        entityType="procedure"
                      />
                      <DenialSection
                        data={procedure.denial}
                        sectionId={`${procedureId}-denial`}
                        entityType="procedure"
                      />
                    </Accordion>
                  )}
                </div>
              </ReviewProvider>
            </ItemDispositionProvider>
          );
        })}
      </div>
    </SchemaProvider>
  );
};

// =============================================================================
// PREVIEW COMPONENT
// =============================================================================

/** Item shape for preview (defined locally, schema provides field definitions at runtime) */
interface ProcedurePreviewItem {
  code?: string;
  name?: string;
}

/**
 * Generate a stable key from item properties.
 * Prefers code, then name, then falls back to index as last resort.
 */
function getStableKey(item: ProcedurePreviewItem, index: number): string {
  if (item.code) return `code-${item.code}`;
  if (item.name) return `name-${item.name}`;
  return `item-${index}`;
}

/**
 * ProcedureOutputFieldPreview - Pill-based preview component for procedure output array field
 *
 * Displays procedures as pills showing code and optionally name. Overflow past
 * `maxItems` is reachable on hover — see {@link PillPreviewList}.
 */
export const ProcedureOutputFieldPreview: React.FC<PreviewProps> = ({ data, maxItems = 2 }) => {
  const items = (Array.isArray(data) ? data : []) as ProcedurePreviewItem[];

  const pills: PreviewPill[] = items.flatMap((procedure, index) => {
    const parts = [procedure.code, procedure.name].filter(Boolean);
    if (parts.length === 0) return [];
    return [
      { key: getStableKey(procedure, index), text: parts.join(' · '), className: 'font-mono' },
    ];
  });

  return <PillPreviewList pills={pills} maxItems={maxItems} />;
};
